import api from "@/services/api";

export interface ChaoServiceDef {
  id: string;
  name: string;
  weight: number;
  progressWeight?: number;
  code?: string;
  parentId?: string;
  isGroup?: boolean;
}

export interface ChaoUnitMetrics {
  generalPct: number;
  status: string;
  photoCount: number;
  lastUpdate: string | null;
  serviceProgress: Record<string, number>;
  pendingApprovalCount?: number;
}

export interface ChaoPhysicalMonthPoint {
  mesKey: string;
  pct: number;
}

export type ChaoPhysicalWeekPoint = ChaoPhysicalMonthPoint;

export interface ChaoMetricsResponse {
  success: boolean;
  serviceCatalog: ChaoServiceDef[];
  byUnit: Record<string, ChaoUnitMetrics>;
  enterpriseGeneralPct: number;
  physicalSeries?: ChaoPhysicalMonthPoint[];
  physicalWeekSeries?: ChaoPhysicalWeekPoint[];
}

export interface ChaoPhotoDto {
  id: string;
  unitId: string;
  ambiente: string;
  storedFile: string;
  mimeType: string;
  observacao: string;
  responsavel: string;
  createdAt: string;
  reportId?: string;
  canEdit?: boolean;
  canDelete?: boolean;
  editableUntil?: string;
}

export function photoCanEdit(p: ChaoPhotoDto): boolean {
  if (p.canEdit === false) return false;
  if (p.canEdit === true) return true;
  if (p.editableUntil) return Date.now() < Date.parse(p.editableUntil);
  return true;
}

export function photoCanDelete(p: ChaoPhotoDto): boolean {
  if (p.canDelete === false) return false;
  if (p.canDelete === true) return true;
  if (p.editableUntil) return Date.now() < Date.parse(p.editableUntil);
  return false;
}

export type ChaoReportStatusDto = "open" | "pending_approval" | "closed";

export interface ChaoReportSummaryDto {
  id: string;
  unitId: string;
  startedAt: string;
  at: string;
  responsavel: string;
  photoCount: number;
  status?: ChaoReportStatusDto;
  isOpen: boolean;
  isPendingApproval?: boolean;
  generalPct?: number;
}

export function reportIsPending(r: Pick<ChaoReportSummaryDto, "status" | "isPendingApproval">): boolean {
  return r.isPendingApproval === true || r.status === "pending_approval";
}

export interface ChaoReportDetailResponse {
  success: boolean;
  report: ChaoReportSummaryDto;
  photos: ChaoPhotoDto[];
  serviceCatalog?: ChaoServiceDef[];
  serviceProgress?: Record<string, number>;
  progressFloor?: Record<string, number>;
  generalPct?: number;
  readOnly?: boolean;
  isPendingApproval?: boolean;
  activeReportId?: string | null;
}

export interface ChaoTimelineEventDto {
  id: string;
  unitId: string;
  at: string;
  type: "photo" | "services";
  title: string;
  detail?: string;
}

export interface ChaoUnitDetailResponse {
  success: boolean;
  unitId: string;
  serviceCatalog: ChaoServiceDef[];
  serviceProgress: Record<string, number>;
  generalPct: number;
  status: string;
  photos: ChaoPhotoDto[];
  events: ChaoTimelineEventDto[];
  reports?: ChaoReportSummaryDto[];
  activeReportId?: string | null;
  photoEditWindowMs?: number;
}

export const CHAO_MAX_PHOTOS_PER_AMBIENTE = 3;
export const CHAO_PHOTO_EDIT_WINDOW_MS = 4 * 60 * 60 * 1000;

export function chaoPhotoFileUrl(photoId: string): string {
  return `/api/chao-de-obra/photo/${encodeURIComponent(photoId)}/file`;
}

export interface ChaoUnitLinksResponse {
  success: boolean;
  costCenterId?: string;
  links?: Record<string, string>;
  error?: string;
}

export async function fetchChaoUnitLinks(costCenterId: string): Promise<ChaoUnitLinksResponse> {
  const { data } = await api.get<ChaoUnitLinksResponse>("/api/chao-de-obra/unit-links", {
    params: { costCenterId },
  });
  return data;
}

export async function saveChaoUnitLinks(
  costCenterId: string,
  links: Record<string, string>,
): Promise<{ success: boolean; error?: string }> {
  try {
    const { data } = await api.put<{ success: boolean; error?: string }>("/api/chao-de-obra/unit-links", {
      costCenterId,
      links,
    });
    return data;
  } catch (e: unknown) {
    const ax = e as { response?: { data?: { error?: string } } };
    return { success: false, error: ax.response?.data?.error ?? "Falha ao salvar vínculos." };
  }
}

export interface ChaoUnitAvgValueResponse {
  success: boolean;
  costCenterId?: string;
  value?: number;
  error?: string;
}

export async function fetchChaoUnitAvgValue(costCenterId: string): Promise<ChaoUnitAvgValueResponse> {
  const { data } = await api.get<ChaoUnitAvgValueResponse>("/api/chao-de-obra/unit-avg-value", {
    params: { costCenterId },
  });
  return data;
}

export async function saveChaoUnitAvgValue(
  costCenterId: string,
  value: number,
): Promise<{ success: boolean; error?: string }> {
  try {
    const { data } = await api.put<{ success: boolean; error?: string }>("/api/chao-de-obra/unit-avg-value", {
      costCenterId,
      value,
    });
    return data;
  } catch (e: unknown) {
    const ax = e as { response?: { data?: { error?: string } } };
    return { success: false, error: ax.response?.data?.error ?? "Falha ao salvar valor médio." };
  }
}

export interface ChaoFinancialDeductionResponse {
  success: boolean;
  costCenterId?: string;
  value?: number;
  error?: string;
}

export async function fetchChaoFinancialDeduction(
  costCenterId: string,
): Promise<ChaoFinancialDeductionResponse> {
  const { data } = await api.get<ChaoFinancialDeductionResponse>("/api/chao-de-obra/financial-deduction", {
    params: { costCenterId },
  });
  return data;
}

export async function saveChaoFinancialDeduction(
  costCenterId: string,
  value: number,
): Promise<{ success: boolean; error?: string }> {
  try {
    const { data } = await api.put<{ success: boolean; error?: string }>(
      "/api/chao-de-obra/financial-deduction",
      { costCenterId, value },
    );
    return data;
  } catch (e: unknown) {
    const ax = e as { response?: { data?: { error?: string } } };
    return { success: false, error: ax.response?.data?.error ?? "Falha ao salvar dedução." };
  }
}

export type ChaoUcStagePhotoKind = "start" | "end";

export interface ChaoUcStagePhotoDto {
  id: string;
  mimeType: string;
  originalName: string;
  createdAt: string;
}

export interface ChaoUcStageScheduleDto {
  startDate: string | null;
  endDate: string | null;
  startPhoto: ChaoUcStagePhotoDto | null;
  endPhoto: ChaoUcStagePhotoDto | null;
}

export function emptyChaoUcStageSchedule(): ChaoUcStageScheduleDto {
  return { startDate: null, endDate: null, startPhoto: null, endPhoto: null };
}

export function chaoUcStagePhotoFileUrl(photoId: string): string {
  return `/api/chao-de-obra/uc-stage-schedule/photo/${encodeURIComponent(photoId)}/file`;
}

export interface ChaoUcStageSchedulesResponse {
  success: boolean;
  costCenterId?: string;
  schedules?: Record<string, ChaoUcStageScheduleDto>;
  error?: string;
}

export async function fetchChaoUcStageSchedules(
  costCenterId: string,
): Promise<ChaoUcStageSchedulesResponse> {
  const { data } = await api.get<ChaoUcStageSchedulesResponse>("/api/chao-de-obra/uc-stage-schedule", {
    params: { costCenterId },
  });
  return data;
}

export async function saveChaoUcStageDates(
  costCenterId: string,
  ucLabel: string,
  startDate: string | null,
  endDate: string | null,
): Promise<{ success: boolean; schedule?: ChaoUcStageScheduleDto; error?: string }> {
  try {
    const { data } = await api.put<{
      success: boolean;
      schedule?: ChaoUcStageScheduleDto;
      error?: string;
    }>("/api/chao-de-obra/uc-stage-schedule", {
      costCenterId,
      ucLabel,
      startDate,
      endDate,
    });
    return data;
  } catch (e: unknown) {
    const ax = e as { response?: { data?: { error?: string } } };
    return { success: false, error: ax.response?.data?.error ?? "Falha ao salvar as datas da etapa." };
  }
}

export async function uploadChaoUcStagePhoto(
  costCenterId: string,
  ucLabel: string,
  kind: ChaoUcStagePhotoKind,
  file: File,
): Promise<{ success: boolean; schedule?: ChaoUcStageScheduleDto; error?: string }> {
  try {
    const fd = new FormData();
    fd.append("foto", file);
    fd.append("costCenterId", costCenterId);
    fd.append("ucLabel", ucLabel);
    fd.append("kind", kind);
    const { data } = await api.post<{
      success: boolean;
      schedule?: ChaoUcStageScheduleDto;
      error?: string;
    }>("/api/chao-de-obra/uc-stage-schedule/photo", fd);
    return data;
  } catch (e: unknown) {
    const ax = e as { response?: { data?: { error?: string } } };
    return { success: false, error: ax.response?.data?.error ?? "Falha ao enviar a foto." };
  }
}

export async function deleteChaoUcStagePhoto(
  costCenterId: string,
  ucLabel: string,
  kind: ChaoUcStagePhotoKind,
): Promise<{ success: boolean; schedule?: ChaoUcStageScheduleDto; error?: string }> {
  try {
    const { data } = await api.delete<{
      success: boolean;
      schedule?: ChaoUcStageScheduleDto;
      error?: string;
    }>("/api/chao-de-obra/uc-stage-schedule/photo", {
      data: { costCenterId, ucLabel, kind },
    });
    return data;
  } catch (e: unknown) {
    const ax = e as { response?: { data?: { error?: string } } };
    return { success: false, error: ax.response?.data?.error ?? "Falha ao remover a foto." };
  }
}

export async function fetchChaoMetrics(
  unitIds: string[],
  options?: { historyMonths?: number; historyWeeks?: number },
): Promise<ChaoMetricsResponse> {
  const params: Record<string, string> = {};
  if (unitIds.length) params.unitIds = unitIds.join(",");
  if (options?.historyMonths && options.historyMonths > 0) {
    params.historyMonths = String(options.historyMonths);
  }
  if (options?.historyWeeks && options.historyWeeks > 0) {
    params.historyWeeks = String(options.historyWeeks);
  }
  const { data } = await api.get<ChaoMetricsResponse>("/api/chao-de-obra/metrics", { params });
  return data;
}

/** Catálogo EAP global (ponderação e estrutura de serviços). */
export async function fetchChaoServiceCatalog(): Promise<ChaoServiceDef[]> {
  const data = await fetchChaoMetrics([]);
  return data.serviceCatalog ?? [];
}

export async function fetchChaoUnitDetail(unitId: string): Promise<ChaoUnitDetailResponse> {
  const { data } = await api.get<ChaoUnitDetailResponse>(
    `/api/chao-de-obra/unit/${encodeURIComponent(unitId)}`
  );
  return data;
}

export async function saveChaoProgressWeights(
  weights: Record<string, number>,
  unitId?: string
): Promise<{
  success: boolean;
  serviceCatalog?: ChaoServiceDef[];
  generalPct?: number;
  error?: string;
}> {
  try {
    const { data } = await api.put<{
      success: boolean;
      serviceCatalog?: ChaoServiceDef[];
      generalPct?: number;
      error?: string;
    }>("/api/chao-de-obra/catalog/progress-weights", { weights, unitId });
    return data;
  } catch (e: unknown) {
    const ax = e as { response?: { data?: { error?: string } } };
    return { success: false, error: ax.response?.data?.error ?? "Falha ao salvar ponderação." };
  }
}

export async function saveChaoServiceProgress(
  unitId: string,
  percentages: Record<string, number>,
  opts?: { medicaoId?: string },
): Promise<{ success: boolean; generalPct?: number; status?: string; error?: string }> {
  const { data } = await api.put(`/api/chao-de-obra/unit/${encodeURIComponent(unitId)}/services`, {
    percentages,
    medicaoId: opts?.medicaoId,
  });
  return data;
}

export async function startChaoReportSession(
  unitId: string
): Promise<{ success: boolean; report?: ChaoReportSummaryDto; activeReportId?: string; error?: string }> {
  try {
    const { data } = await api.post<{
      success: boolean;
      report?: ChaoReportSummaryDto;
      activeReportId?: string;
      error?: string;
    }>(`/api/chao-de-obra/unit/${encodeURIComponent(unitId)}/report/start`);
    return data;
  } catch (e: unknown) {
    const ax = e as { response?: { data?: { error?: string }; status?: number } };
    return { success: false, error: ax.response?.data?.error ?? "Falha ao iniciar registro." };
  }
}

type ChaoReportWorkflowResult = {
  success: boolean;
  report?: ChaoReportSummaryDto;
  activeReportId?: string | null;
  generalPct?: number;
  status?: string;
  error?: string;
  missingAmbientes?: string[];
};

export async function submitChaoReportForApproval(
  unitId: string,
  reportId: string,
  percentages: Record<string, number>,
): Promise<ChaoReportWorkflowResult> {
  try {
    const { data } = await api.post<ChaoReportWorkflowResult>(
      `/api/chao-de-obra/unit/${encodeURIComponent(unitId)}/report/${encodeURIComponent(reportId)}/submit`,
      { percentages },
    );
    return data;
  } catch (e: unknown) {
    const ax = e as { response?: { data?: { error?: string; missingAmbientes?: string[] } } };
    const body = ax.response?.data;
    return {
      success: false,
      error: body?.error ?? "Falha ao enviar o registro para aprovação.",
      missingAmbientes: body?.missingAmbientes,
    };
  }
}

export async function approveChaoReport(
  unitId: string,
  reportId: string,
): Promise<ChaoReportWorkflowResult> {
  try {
    const { data } = await api.post<ChaoReportWorkflowResult>(
      `/api/chao-de-obra/unit/${encodeURIComponent(unitId)}/report/${encodeURIComponent(reportId)}/approve`,
    );
    return data;
  } catch (e: unknown) {
    const ax = e as { response?: { data?: { error?: string; missingAmbientes?: string[] } } };
    const body = ax.response?.data;
    return {
      success: false,
      error: body?.error ?? "Falha ao aprovar o registro.",
      missingAmbientes: body?.missingAmbientes,
    };
  }
}

export async function rejectChaoReport(
  unitId: string,
  reportId: string,
  note?: string,
): Promise<ChaoReportWorkflowResult> {
  try {
    const { data } = await api.post<ChaoReportWorkflowResult>(
      `/api/chao-de-obra/unit/${encodeURIComponent(unitId)}/report/${encodeURIComponent(reportId)}/reject`,
      { note },
    );
    return data;
  } catch (e: unknown) {
    const ax = e as { response?: { data?: { error?: string } } };
    return { success: false, error: ax.response?.data?.error ?? "Falha ao devolver o registro." };
  }
}

export async function uploadChaoPhoto(
  unitId: string,
  file: File,
  ambiente: string,
  observacao: string
): Promise<{ success: boolean; photoId?: string; error?: string }> {
  const fd = new FormData();
  fd.append("foto", file);
  fd.append("ambiente", ambiente);
  fd.append("observacao", observacao);
  const { data } = await api.post(`/api/chao-de-obra/unit/${encodeURIComponent(unitId)}/photo`, fd);
  return data;
}

export function chaoReportPath(
  unitId: string,
  reportId: string,
  tab: "photos" | "progress" = "photos",
): string {
  const qs = new URLSearchParams({ relatorio: reportId });
  if (tab === "progress") qs.set("aba", "progress");
  return `/chao-de-obra/unidade/${encodeURIComponent(unitId)}?${qs.toString()}`;
}

export async function saveChaoReportDraftProgress(
  unitId: string,
  reportId: string,
  percentages: Record<string, number>
): Promise<{ success: boolean; generalPct?: number; error?: string }> {
  try {
    const { data } = await api.put<{ success: boolean; generalPct?: number; error?: string }>(
      `/api/chao-de-obra/unit/${encodeURIComponent(unitId)}/report/${encodeURIComponent(reportId)}/progress`,
      { percentages }
    );
    return data;
  } catch (e: unknown) {
    const ax = e as { response?: { data?: { error?: string } } };
    return { success: false, error: ax.response?.data?.error ?? "Falha ao salvar rascunho." };
  }
}

export async function fetchChaoReportDetail(
  unitId: string,
  reportId: string
): Promise<ChaoReportDetailResponse> {
  const { data } = await api.get<ChaoReportDetailResponse>(
    `/api/chao-de-obra/unit/${encodeURIComponent(unitId)}/report/${encodeURIComponent(reportId)}`
  );
  return data;
}

export async function updateChaoPhotoObservacao(
  photoId: string,
  observacao: string
): Promise<{ success: boolean; photo?: ChaoPhotoDto; error?: string }> {
  try {
    const { data } = await api.patch<{ success: boolean; photo?: ChaoPhotoDto; error?: string }>(
      `/api/chao-de-obra/photo/${encodeURIComponent(photoId)}`,
      { observacao }
    );
    return data;
  } catch (e: unknown) {
    const ax = e as { response?: { data?: { error?: string } } };
    const fromApi = ax.response?.data?.error;
    if (fromApi) return { success: false, error: fromApi };
    if (e instanceof Error) return { success: false, error: e.message };
    return { success: false, error: "Erro ao atualizar a foto." };
  }
}

export async function deleteChaoPhoto(
  photoId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { data } = await api.delete<{ success: boolean; error?: string }>(
      `/api/chao-de-obra/photo/${encodeURIComponent(photoId)}`
    );
    return data;
  } catch (e: unknown) {
    const ax = e as { response?: { data?: { error?: string } } };
    const fromApi = ax.response?.data?.error;
    if (fromApi) return { success: false, error: fromApi };
    if (e instanceof Error) return { success: false, error: e.message };
    return { success: false, error: "Erro ao excluir a foto." };
  }
}

export async function deleteChaoReport(
  unitId: string,
  reportId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { data } = await api.delete<{ success: boolean; error?: string }>(
      `/api/chao-de-obra/unit/${encodeURIComponent(unitId)}/report/${encodeURIComponent(reportId)}`
    );
    return data;
  } catch (e: unknown) {
    const ax = e as { response?: { data?: { error?: string } } };
    const fromApi = ax.response?.data?.error;
    if (fromApi) return { success: false, error: fromApi };
    if (e instanceof Error) return { success: false, error: e.message };
    return { success: false, error: "Erro ao excluir o registro." };
  }
}

export type ChaoStatusCelula =
  | "pendencia"
  | "atestado"
  | "concluido"
  | "atrasado"
  | "execucao"
  | "previsto"
  | "nao_iniciado";

export type ChaoPendenciaMotivo =
  | "retrabalho"
  | "reprovado_vistoria"
  | "bloqueado_predecessor"
  | "falta_material"
  | "outro";

export interface ChaoMapaCell {
  percentualReal: number;
  percentualPrevistoAteMedicaoAtual: number | null;
  percentualPrevistoMedicaoSelecionada: number | null;
  percentualAtestado: number | null;
  status: ChaoStatusCelula;
  pendencia: { id: string; motivo: ChaoPendenciaMotivo } | null;
  lastUpdate: { at: string; usuarioNome: string } | null;
  inconsistent: boolean;
}

export interface ChaoMapaRow {
  serviceId: string;
  code: string;
  name: string;
  parentId?: string;
  concludedCount: number;
  total: number;
  avgPct: number;
}

export interface ChaoMapaColumn {
  unitId: string;
  generalPct: number;
}

export interface ChaoMapaMedicaoTab {
  id: string;
  numero: number;
  dataInicio: string;
  dataFim: string;
  status: "ABERTA" | "FECHADA" | "ATESTADA";
  dataAtesto?: string;
  previstosAcumulados: number;
  isCurrent: boolean;
}

export interface ChaoMapaSummary {
  medicaoNumero: number | null;
  dataInicio: string | null;
  dataFim: string | null;
  previstos: number;
  concluidos: number;
  faltam: number;
  atrasados: number;
  pendencias: number;
  avancoFisico: number;
  previstoPct: number;
  lastAtestadaNumero: number | null;
}

export interface ChaoMapaPayload {
  success: boolean;
  error?: string;
  costCenterId: string;
  atrasoTolerancePp: number;
  medicoes: ChaoMapaMedicaoTab[];
  selectedMedicaoId: string | null;
  currentMedicaoId: string | null;
  columns: ChaoMapaColumn[];
  rows: ChaoMapaRow[];
  cells: Record<string, Record<string, ChaoMapaCell>>;
  summary: ChaoMapaSummary;
  curvaS: { medicaoId: string; numero: number; previstoPct: number; realizadoPct: number }[];
  avancoObra: number;
  canEditPct: boolean;
  canEditPlanejamento: boolean;
  canAtestar: boolean;
}

export async function fetchChaoMapa(opts: {
  costCenterId: string;
  unitIds: string[];
  medicaoId?: string | null;
}): Promise<ChaoMapaPayload> {
  const params = new URLSearchParams();
  params.set("costCenterId", opts.costCenterId);
  if (opts.unitIds.length) params.set("unitIds", opts.unitIds.join(","));
  if (opts.medicaoId) params.set("medicaoId", opts.medicaoId);
  const { data } = await api.get<ChaoMapaPayload>(`/api/chao-de-obra/mapa?${params.toString()}`);
  return data;
}

export interface ChaoMapaCelulaDetalhe {
  success: boolean;
  error?: string;
  historico: {
    id: string;
    percentualAnterior: number;
    percentualNovo: number;
    usuarioNome: string;
    createdAt: string;
    medicaoId?: string;
  }[];
  pendencias: {
    id: string;
    motivo: ChaoPendenciaMotivo;
    detalhe?: string;
    status: "aberta" | "resolvida";
    createdAt: string;
    createdBy: string;
    resolvedAt?: string;
    resolvedBy?: string;
  }[];
  previsoes: { medicaoId: string; numero: number; percentualPrevisto: number }[];
  atestos: { medicaoId: string; numero: number; percentualAtestado: number; dataAtesto?: string }[];
  service: ChaoServiceDef | null;
}

export async function fetchChaoMapaCelula(opts: {
  costCenterId: string;
  unitId: string;
  serviceId: string;
}): Promise<ChaoMapaCelulaDetalhe> {
  const params = new URLSearchParams(opts);
  const { data } = await api.get<ChaoMapaCelulaDetalhe>(
    `/api/chao-de-obra/mapa/celula?${params.toString()}`,
  );
  return data;
}

export async function saveChaoMapaProgressBatch(
  items: { unitId: string; serviceId: string; percentual: number }[],
  medicaoId?: string,
): Promise<{ success: boolean; generalByUnit?: Record<string, number>; error?: string }> {
  try {
    const { data } = await api.put("/api/chao-de-obra/mapa/progress", { items, medicaoId });
    return data;
  } catch (e: unknown) {
    const ax = e as { response?: { data?: { error?: string } } };
    return { success: false, error: ax.response?.data?.error ?? "Falha ao salvar avanço." };
  }
}

export async function createChaoMedicao(input: {
  costCenterId: string;
  dataInicio: string;
  dataFim: string;
  numero?: number;
}): Promise<{ success: boolean; medicao?: ChaoMapaMedicaoTab; error?: string }> {
  try {
    const { data } = await api.post("/api/chao-de-obra/mapa/medicoes", input);
    return data;
  } catch (e: unknown) {
    const ax = e as { response?: { data?: { error?: string } } };
    return { success: false, error: ax.response?.data?.error ?? "Falha ao criar medição." };
  }
}

export async function patchChaoMedicao(
  id: string,
  patch: { dataInicio?: string; dataFim?: string; status?: "ABERTA" | "FECHADA" | "ATESTADA" },
): Promise<{ success: boolean; error?: string }> {
  try {
    const { data } = await api.patch(`/api/chao-de-obra/mapa/medicoes/${encodeURIComponent(id)}`, patch);
    return data;
  } catch (e: unknown) {
    const ax = e as { response?: { data?: { error?: string } } };
    return { success: false, error: ax.response?.data?.error ?? "Falha ao atualizar medição." };
  }
}

export async function atestarChaoMedicao(
  id: string,
  unitIds: string[],
): Promise<{ success: boolean; error?: string }> {
  try {
    const { data } = await api.post(`/api/chao-de-obra/mapa/medicoes/${encodeURIComponent(id)}/atestar`, {
      unitIds,
    });
    return data;
  } catch (e: unknown) {
    const ax = e as { response?: { data?: { error?: string } } };
    return { success: false, error: ax.response?.data?.error ?? "Falha ao atestar medição." };
  }
}

export async function saveChaoPrevisao(input: {
  servicoId: string;
  casaId: string;
  medicaoId: string;
  percentualPrevisto: number;
}): Promise<{ success: boolean; error?: string }> {
  try {
    const { data } = await api.put("/api/chao-de-obra/mapa/previsao", input);
    return data;
  } catch (e: unknown) {
    const ax = e as { response?: { data?: { error?: string } } };
    return { success: false, error: ax.response?.data?.error ?? "Falha ao salvar previsão." };
  }
}

export async function saveChaoPrevisoesBatch(
  items: { servicoId: string; casaId: string; medicaoId: string; percentualPrevisto: number }[],
): Promise<{ success: boolean; error?: string }> {
  try {
    const { data } = await api.put("/api/chao-de-obra/mapa/previsoes", { items });
    return data;
  } catch (e: unknown) {
    const ax = e as { response?: { data?: { error?: string } } };
    return { success: false, error: ax.response?.data?.error ?? "Falha ao salvar previsões." };
  }
}

export async function abrirChaoPendencia(input: {
  servicoId: string;
  casaId: string;
  motivo: ChaoPendenciaMotivo;
  detalhe?: string;
}): Promise<{ success: boolean; error?: string }> {
  try {
    const { data } = await api.post("/api/chao-de-obra/mapa/pendencias", input);
    return data;
  } catch (e: unknown) {
    const ax = e as { response?: { data?: { error?: string } } };
    return { success: false, error: ax.response?.data?.error ?? "Falha ao abrir pendência." };
  }
}

export async function resolverChaoPendencia(id: string): Promise<{ success: boolean; error?: string }> {
  try {
    const { data } = await api.post(`/api/chao-de-obra/mapa/pendencias/${encodeURIComponent(id)}/resolver`);
    return data;
  } catch (e: unknown) {
    const ax = e as { response?: { data?: { error?: string } } };
    return { success: false, error: ax.response?.data?.error ?? "Falha ao resolver pendência." };
  }
}

export type ChaoRegraBaseAvanco = "ULTIMA_ANTERIOR" | "PRIMEIRA_DO_PERIODO";
export type ChaoProdutividadeStatus = "atingiu" | "atencao" | "abaixo" | "sem_meta";

export interface ChaoProdutividadePonto {
  at: string | null;
  pct: number;
}

export interface ChaoPeriodoCalculado {
  id: string;
  costCenterId: string;
  competencia: string;
  numero: number;
  descricao?: string;
  dataInicio: string;
  dataFim: string;
  metaAvancoPp: number;
  metaObraPp?: number;
  metaPorUc?: Record<string, number>;
  constructiveUnit?: string;
  observacao?: string;
  base: ChaoProdutividadePonto;
  final: ChaoProdutividadePonto;
  avancoRealPp: number;
  desvioPp: number;
  atingimentoPct: number | null;
  status: ChaoProdutividadeStatus;
  semMedicao: boolean;
}

export interface ChaoProdutividadeTotais {
  avancoRealMesPp: number;
  metaMesPp: number;
  atingimentoMesPct: number | null;
  semanasAtingidas: number;
  totalSemanas: number;
}

export interface ChaoSerieAcumuladaPonto {
  numero: number;
  descricao?: string;
  dataInicio: string;
  dataFim: string;
  realAcumuladoPp: number;
  metaAcumuladaPp: number;
  atingimentoPct: number | null;
}

export interface ChaoProdutividadeMes {
  success: boolean;
  error?: string;
  regraBase: ChaoRegraBaseAvanco;
  periodos: ChaoPeriodoCalculado[];
  totais: ChaoProdutividadeTotais;
  serieAcumulada: ChaoSerieAcumuladaPonto[];
  warnings: string[];
}

export interface ChaoProdutividadeAnoMes {
  competencia: string;
  avancoRealMesPp: number;
  metaMesPp: number;
  atingimentoMesPct: number | null;
}

export interface ChaoPeriodoAvancoInput {
  costCenterId: string;
  competencia: string;
  numero: number;
  descricao?: string;
  dataInicio: string;
  dataFim: string;
  metaAvancoPp: number;
  metaPorUc?: Record<string, number>;
  observacao?: string;
}

export interface ChaoPeriodoSugerido {
  numero: number;
  descricao: string;
  dataInicio: string;
  dataFim: string;
  metaAvancoPp: number;
  metaPorUc?: Record<string, number>;
}

function chaoApiError(e: unknown, fallback: string): string {
  const ax = e as { response?: { data?: { error?: string } } };
  return ax.response?.data?.error ?? fallback;
}

function unitIdsParam(unitIds: string[]): string {
  return unitIds.map((id) => id.trim()).filter(Boolean).join(",");
}

export async function fetchChaoProdutividade(opts: {
  costCenterId: string;
  competencia: string;
  unitIds: string[];
  constructiveUnit?: string;
}): Promise<ChaoProdutividadeMes> {
  try {
    const params = new URLSearchParams({
      costCenterId: opts.costCenterId,
      competencia: opts.competencia,
      unitIds: unitIdsParam(opts.unitIds),
    });
    if (opts.constructiveUnit) params.set("constructiveUnit", opts.constructiveUnit);
    const { data } = await api.get<ChaoProdutividadeMes>(`/api/chao-de-obra/produtividade?${params.toString()}`);
    return data;
  } catch (e: unknown) {
    return {
      success: false,
      error: chaoApiError(e, "Falha ao carregar a produtividade."),
      regraBase: "ULTIMA_ANTERIOR",
      periodos: [],
      totais: {
        avancoRealMesPp: 0,
        metaMesPp: 0,
        atingimentoMesPct: null,
        semanasAtingidas: 0,
        totalSemanas: 0,
      },
      serieAcumulada: [],
      warnings: [],
    };
  }
}

export async function fetchChaoProdutividadeAnual(opts: {
  costCenterId: string;
  unitIds: string[];
  constructiveUnit?: string;
}): Promise<{ success: boolean; meses: ChaoProdutividadeAnoMes[]; error?: string }> {
  try {
    const params = new URLSearchParams({
      costCenterId: opts.costCenterId,
      unitIds: unitIdsParam(opts.unitIds),
    });
    if (opts.constructiveUnit) params.set("constructiveUnit", opts.constructiveUnit);
    const { data } = await api.get<{ success: boolean; meses: ChaoProdutividadeAnoMes[] }>(
      `/api/chao-de-obra/produtividade/anual?${params.toString()}`,
    );
    return data;
  } catch (e: unknown) {
    return { success: false, meses: [], error: chaoApiError(e, "Falha ao carregar a visão anual.") };
  }
}

export async function sugerirChaoPeriodos(
  competencia: string,
  recorte: "mes" | "quinzena" | "semana" | "dia" = "semana",
): Promise<{ success: boolean; periodos: ChaoPeriodoSugerido[]; error?: string }> {
  try {
    const { data } = await api.get<{ success: boolean; periodos: ChaoPeriodoSugerido[] }>(
      "/api/chao-de-obra/produtividade/sugerir-periodos",
      { params: { competencia, recorte } },
    );
    return data;
  } catch (e: unknown) {
    return { success: false, periodos: [], error: chaoApiError(e, "Falha ao montar os períodos.") };
  }
}

export async function createChaoPeriodoAvanco(
  input: ChaoPeriodoAvancoInput,
): Promise<{ success: boolean; error?: string }> {
  try {
    const { data } = await api.post("/api/chao-de-obra/periodos-avanco", input);
    return data;
  } catch (e: unknown) {
    return { success: false, error: chaoApiError(e, "Falha ao criar o período.") };
  }
}

export async function updateChaoPeriodoAvanco(
  id: string,
  patch: Partial<ChaoPeriodoAvancoInput>,
): Promise<{ success: boolean; error?: string }> {
  try {
    const { data } = await api.put(`/api/chao-de-obra/periodos-avanco/${encodeURIComponent(id)}`, patch);
    return data;
  } catch (e: unknown) {
    return { success: false, error: chaoApiError(e, "Falha ao atualizar o período.") };
  }
}

export async function deleteChaoPeriodoAvanco(id: string): Promise<{ success: boolean; error?: string }> {
  try {
    const { data } = await api.delete(`/api/chao-de-obra/periodos-avanco/${encodeURIComponent(id)}`);
    return data;
  } catch (e: unknown) {
    return { success: false, error: chaoApiError(e, "Falha ao excluir o período.") };
  }
}

export async function saveChaoPeriodosLote(input: {
  costCenterId: string;
  competencia: string;
  periodos: ChaoPeriodoSugerido[];
  unitIds: string[];
  substituir?: boolean;
  constructiveUnit?: string;
}): Promise<{ success: boolean; warnings?: string[]; error?: string }> {
  try {
    const { data } = await api.post("/api/chao-de-obra/periodos-avanco/lote", input);
    return data;
  } catch (e: unknown) {
    return { success: false, error: chaoApiError(e, "Falha ao salvar as semanas.") };
  }
}

export async function saveChaoRegraBaseAvanco(
  costCenterId: string,
  regra: ChaoRegraBaseAvanco,
): Promise<{ success: boolean; error?: string }> {
  try {
    const { data } = await api.put("/api/chao-de-obra/produtividade/regra-base", { costCenterId, regra });
    return data;
  } catch (e: unknown) {
    return { success: false, error: chaoApiError(e, "Falha ao salvar a regra de base.") };
  }
}
